import * as React from 'react'
import { useImperativeHandle } from 'react'

import { Label } from 'src/shared/components/ui/label'
import { cn } from 'src/shared/utils/cn'

interface UseAutosizeTextareaProps {
  textAreaRef: React.MutableRefObject<HTMLTextAreaElement | null>
  minRows?: number
  maxRows?: number
  triggerAutoSize: string
}

const ROW_HEIGT = 45

export const useAutosizeTextarea = ({
  textAreaRef,
  triggerAutoSize,
  maxRows = 9,
  minRows = 1,
}: UseAutosizeTextareaProps) => {
  const [init, setInit] = React.useState(true)

  const minHeight = minRows * ROW_HEIGT
  const maxHeight = maxRows * ROW_HEIGT

  React.useEffect(() => {
    const offsetBorder = 2
    const textAreaElement = textAreaRef.current

    if (!textAreaElement) return

    // Create a local style object to track changes
    const newStyle = {
      minHeight: `${minHeight + offsetBorder}px`,
      height: `${minHeight + offsetBorder}px`,
      maxHeight: maxHeight > minHeight ? `${maxHeight}px` : undefined,
    }

    if (init) {
      Object.assign(textAreaElement.style, {
        minHeight: newStyle.minHeight,
        maxHeight: newStyle.maxHeight,
      })
      setInit(false)
    }

    // Create a local style object for the current update
    const currentStyle = {
      ...newStyle,
      height: 'auto',
    }

    // Apply styles using Object.assign to avoid direct mutations
    Object.assign(textAreaElement.style, currentStyle)
    const scrollHeight = textAreaElement.scrollHeight

    // Calculate the new height
    let newHeight = `${minHeight + offsetBorder}px`
    if (triggerAutoSize.trim() && scrollHeight > minHeight) {
      newHeight = scrollHeight > maxHeight ? `${maxHeight}px` : `${scrollHeight + offsetBorder}px`
    }

    // Apply the final height using Object.assign
    Object.assign(textAreaElement.style, { height: newHeight })
  }, [textAreaRef, triggerAutoSize, init, minHeight, maxHeight])
}

export type AutosizeTextareaRef = {
  textArea: HTMLTextAreaElement
  maxHeight: number
  minHeight: number
}

type AutosizeTextareaProps = {
  maxRows?: number
  minRows?: number
  label?: string
  floating?: boolean
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>

export const AutosizeTextarea = React.forwardRef<AutosizeTextareaRef, AutosizeTextareaProps>(
  (
    { maxRows = 9, minRows = 1, className, onChange, value, label, floating = false, ...props }: AutosizeTextareaProps,
    ref: React.Ref<AutosizeTextareaRef>,
  ) => {
    const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null)
    const [triggerAutoSize, setTriggerAutoSize] = React.useState('')

    const minHeight = minRows * ROW_HEIGT
    const maxHeight = maxRows * ROW_HEIGT

    useAutosizeTextarea({
      textAreaRef,
      triggerAutoSize: triggerAutoSize,
      maxRows,
      minRows,
    })

    useImperativeHandle(ref, () => ({
      textArea: textAreaRef.current as HTMLTextAreaElement,
      focus: () => textAreaRef?.current?.focus(),
      maxHeight,
      minHeight,
    }))

    React.useEffect(() => {
      setTriggerAutoSize(value as string)
    }, [value])

    React.useEffect(() => {
      if (textAreaRef.current) {
        setTriggerAutoSize(textAreaRef.current.value)
      }
    }, [])

    return (
      <div className="relative w-full">
        <textarea
          {...props}
          value={value}
          ref={textAreaRef}
          placeholder={floating ? ' ' : props.placeholder}
          className={cn(
            'peer flex w-full resize-none rounded-lg border border-input bg-background px-3 py-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          style={{ minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px` }}
          onChange={(e) => {
            setTriggerAutoSize(e.target.value)
            onChange?.(e)
          }}
        />
        {label && floating && (
          <Label
            htmlFor={props.id}
            className={cn(
              'absolute left-3 z-10 text-muted duration-300',
              floating
                ? 'top-2 origin-[0] -translate-y-4 scale-75 cursor-text bg-background px-2 text-sm font-medium peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-primary dark:bg-background peer-focus:dark:text-primary'
                : 'top-0 -translate-y-full text-sm',
            )}
          >
            {label}
          </Label>
        )}
      </div>
    )
  },
)
AutosizeTextarea.displayName = 'AutosizeTextarea'
